import React, { useState } from 'react';
import { Plus, X } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Separator } from './ui/separator';

function JobForm({ onSubmit }) {
  const [formData, setFormData] = useState({
    company_name: '',
    job_description: '',
    position_title: '',
    min_years: '',
    max_years: '',
    required_skills: '',
    preferred_skills: '',
    ideal_candidate_spec: '',
    critical_skills: []
  });

  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleCriticalSkillChange = (index, field, value) => {
    const updated = [...formData.critical_skills];
    updated[index] = {
      ...updated[index],
      [field]: value
    };
    setFormData(prev => ({
      ...prev,
      critical_skills: updated
    }));
  };

  const addCriticalSkill = () => {
    setFormData(prev => ({
      ...prev,
      critical_skills: [...prev.critical_skills, { name: '', weight: '2.0' }]
    }));
  };

  const removeCriticalSkill = (index) => {
    setFormData(prev => ({
      ...prev,
      critical_skills: prev.critical_skills.filter((_, i) => i !== index)
    }));
  };

  const validate = () => {
    const newErrors = {};
    
    if (!formData.company_name.trim()) {
      newErrors.company_name = 'Company Name is required';
    }
    if (!formData.job_description.trim()) {
      newErrors.job_description = 'Job Description is required';
    }
    if (!formData.position_title.trim()) {
      newErrors.position_title = 'Position Title is required';
    }
    
    formData.critical_skills.forEach((skill, index) => {
      if (skill.name && (!skill.weight || isNaN(skill.weight) || parseFloat(skill.weight) < 1.0 || parseFloat(skill.weight) > 5.0)) {
        newErrors[`critical_skill_${index}`] = 'Weight must be between 1.0 and 5.0';
      }
    });
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validate()) {
      return;
    }

    const required_skills = formData.required_skills
      .split(';')
      .map(s => s.trim())
      .filter(s => s.length > 0);
    
    const preferred_skills = formData.preferred_skills
      .split(';')
      .map(s => s.trim())
      .filter(s => s.length > 0);

    const critical_skills = formData.critical_skills
      .filter(skill => skill.name && skill.name.trim())
      .map(skill => ({
        name: skill.name.trim(),
        weight: parseFloat(skill.weight) || 2.0
      }));

    onSubmit({
      company_name: formData.company_name.trim(),
      job_description: formData.job_description.trim(),
      position_title: formData.position_title.trim(),
      min_years: formData.min_years ? parseInt(formData.min_years) : null,
      max_years: formData.max_years ? parseInt(formData.max_years) : null,
      required_skills,
      preferred_skills,
      critical_skills,
      ideal_candidate_spec: formData.ideal_candidate_spec.trim() || null
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Required Fields */}
      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="company_name">
            Company Name <span className="text-destructive">*</span>
          </Label>
          <Input
            id="company_name"
            name="company_name"
            value={formData.company_name}
            onChange={handleChange}
            placeholder="e.g., Aeliasoft"
            className={errors.company_name ? 'border-destructive' : ''}
          />
          {errors.company_name && (
            <p className="text-sm text-destructive">{errors.company_name}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="position_title">
            Position Title <span className="text-destructive">*</span>
          </Label>
          <Input
            id="position_title"
            name="position_title"
            value={formData.position_title}
            onChange={handleChange}
            placeholder="e.g., QA Automation Engineer"
            className={errors.position_title ? 'border-destructive' : ''}
          />
          {errors.position_title && (
            <p className="text-sm text-destructive">{errors.position_title}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="job_description">
            Job Description <span className="text-destructive">*</span>
          </Label>
          <Textarea
            id="job_description"
            name="job_description"
            value={formData.job_description}
            onChange={handleChange}
            placeholder="Enter the full job description..."
            className={`min-h-[120px] ${errors.job_description ? 'border-destructive' : ''}`}
          />
          {errors.job_description && (
            <p className="text-sm text-destructive">{errors.job_description}</p>
          )}
        </div>
      </div>

      <Separator />

      {/* Optional Fields */}
      <div className="space-y-4">
        <h3 className="text-sm font-medium text-muted-foreground">Optional Details</h3>
        
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="min_years">Min Years Experience</Label>
            <Input
              type="number"
              id="min_years"
              name="min_years"
              value={formData.min_years}
              onChange={handleChange}
              min="0"
              placeholder="e.g., 3"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="max_years">Max Years Experience</Label>
            <Input
              type="number"
              id="max_years"
              name="max_years"
              value={formData.max_years}
              onChange={handleChange}
              min="0"
              placeholder="e.g., 8"
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="required_skills">Required Skills</Label>
          <Input
            id="required_skills"
            name="required_skills"
            value={formData.required_skills}
            onChange={handleChange}
            placeholder="Separate with semicolons: Python;Playwright;Docker"
          />
          <p className="text-xs text-muted-foreground">Separate multiple skills with semicolons (;)</p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="preferred_skills">Preferred Skills</Label>
          <Input
            id="preferred_skills"
            name="preferred_skills"
            value={formData.preferred_skills}
            onChange={handleChange}
            placeholder="Separate with semicolons: Selenium;Kubernetes"
          />
          <p className="text-xs text-muted-foreground">Separate multiple skills with semicolons (;)</p>
        </div>

        {/* Critical Skills */}
        <div className="space-y-3">
          <Label>Critical Skills</Label>
          <p className="text-xs text-muted-foreground">Add skills with importance weights (1.0 - 5.0)</p>
          
          {formData.critical_skills.map((skill, index) => (
            <div key={index} className="flex items-center gap-2">
              <Input
                placeholder="Skill name (e.g., Playwright)"
                value={skill.name}
                onChange={(e) => handleCriticalSkillChange(index, 'name', e.target.value)}
                className="flex-1"
              />
              <Input
                type="number"
                placeholder="Weight"
                value={skill.weight}
                onChange={(e) => handleCriticalSkillChange(index, 'weight', e.target.value)}
                min="1.0"
                max="5.0"
                step="0.1"
                className="w-24"
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                onClick={() => removeCriticalSkill(index)}
                className="text-destructive hover:text-destructive hover:bg-destructive/10"
              >
                <X className="h-4 w-4" />
              </Button>
              {errors[`critical_skill_${index}`] && (
                <p className="text-sm text-destructive">{errors[`critical_skill_${index}`]}</p>
              )}
            </div>
          ))}
          
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={addCriticalSkill}
            className="gap-1"
          >
            <Plus className="h-4 w-4" />
            Add Critical Skill
          </Button>
        </div>

        <div className="space-y-2">
          <Label htmlFor="ideal_candidate_spec">Ideal Candidate Specification</Label>
          <Textarea
            id="ideal_candidate_spec"
            name="ideal_candidate_spec"
            value={formData.ideal_candidate_spec}
            onChange={handleChange}
            placeholder="e.g., QA professional with 3+ years dedicated test automation experience using Python and Playwright"
            className="min-h-[80px]"
          />
        </div>
      </div>

      <Separator />

      {/* Submit Button */}
      <div className="flex justify-end">
        <Button type="submit" size="lg" className="bg-emerald-600 hover:bg-emerald-700">
          Run Screening
        </Button>
      </div>
    </form>
  );
}

export default JobForm;
